import { ref } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import type { LintRule, ScriptingGuideline, ScriptingGuidelinePayload } from "@/types";
import { captureEvent } from "@/lib/analytics";

interface GuidelinesIndex {
  scripting_guidelines: ScriptingGuideline[];
  lint_rules: LintRule[];
}

export const useScriptingGuidelinesStore = defineStore("scriptingGuidelines", () => {
  const general = ref<ScriptingGuideline[]>([]);
  const accountRules = ref<ScriptingGuideline[]>([]);
  const lintRules = ref<LintRule[]>([]);
  const loading = ref(false);

  async function fetch(accountId: number | null = null) {
    loading.value = true;
    const query = accountId ? `?account_id=${accountId}` : "";
    const { response, data } = await useMyFetch(`/scripting_guidelines${query}`).json<GuidelinesIndex>();
    if (response.value?.ok && data.value) {
      general.value = data.value.scripting_guidelines.filter((guideline) => !guideline.account_id);
      accountRules.value = data.value.scripting_guidelines.filter((guideline) => guideline.account_id);
      lintRules.value = data.value.lint_rules;
    }
    loading.value = false;
  }

  const listOf = (guideline: ScriptingGuideline) => (guideline.account_id ? accountRules : general);

  async function create(payload: ScriptingGuidelinePayload) {
    const { data, error } = await useMyFetch("/scripting_guidelines")
      .post({ scripting_guideline: payload })
      .json<ScriptingGuideline>();
    if (error.value) throw error.value;
    if (!data.value) return;
    listOf(data.value).value.push(data.value);
    captureEvent("scripting_guideline_created", { guideline_id: data.value.id, account_id: data.value.account_id });
  }

  async function update(guideline: ScriptingGuideline, payload: ScriptingGuidelinePayload) {
    const { data, error } = await useMyFetch(`/scripting_guidelines/${guideline.id}`)
      .patch({ scripting_guideline: payload })
      .json<ScriptingGuideline>();
    if (error.value) throw error.value;
    Object.assign(guideline, data.value);
    captureEvent("scripting_guideline_updated", { guideline_id: guideline.id });
  }

  async function destroy(guideline: ScriptingGuideline) {
    const { error } = await useMyFetch(`/scripting_guidelines/${guideline.id}`).delete();
    if (error.value) throw error.value;
    const list = listOf(guideline);
    list.value = list.value.filter((other) => other.id !== guideline.id);
    captureEvent("scripting_guideline_deleted", { guideline_id: guideline.id });
  }

  return { general, accountRules, lintRules, loading, fetch, create, update, destroy };
});
